import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  AlertCircle,
  Plus,
  Trash2,
  GripVertical,
  Settings,
  Eye,
  EyeOff,
  Save,
  ArrowLeft,
  Copy,
  FileText,
  Type,
  Image as ImageIcon,
  Minus,
  MoreVertical,
  RotateCcw,
  RotateCw,
  AlertTriangle,
  Check,
  Layers,
  PanelRightOpen,
  PanelRightClose,
  Shield,
  Users,
  CheckSquare,
  AlertOctagon,
  Clipboard,
  HardHat,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../services/apiService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Block presets for quick insertion
const BLOCK_PRESETS = {
  'hazard_section': {
    name: 'Hazard Section',
    nameEn: 'Hazard Section',
    blocks: [
      { block_type: 'header', content_pl: 'Identyfikacja zagrożeń', content_en: 'Hazard Identification', is_bold: true, font_size: 14 },
      { block_type: 'hazard', content_pl: '', content_en: '', settings: { hazards: [] } },
    ]
  },
  'control_section': {
    name: 'Control Measures Section',
    nameEn: 'Control Measures Section',
    blocks: [
      { block_type: 'header', content_pl: 'Środki kontroli', content_en: 'Control Measures', is_bold: true, font_size: 14 },
      { block_type: 'control_measures', content_pl: '', content_en: '', settings: { measures: [] } },
    ]
  },
  'ppe_section': {
    name: 'PPE Requirements',
    nameEn: 'PPE Requirements',
    blocks: [
      { block_type: 'ppe_requirements', content_pl: '', content_en: '', settings: { selected: [] } },
    ]
  },
  'emergency_section': {
    name: 'Emergency Procedures',
    nameEn: 'Emergency Procedures',
    blocks: [
      { block_type: 'header', content_pl: 'Procedury awaryjne', content_en: 'Emergency Procedures', is_bold: true, font_size: 14 },
      { block_type: 'emergency_procedures', content_pl: '', content_en: '' },
    ]
  },
  'approval_section': {
    name: 'Approval Signatures',
    nameEn: 'Approval Signatures',
    blocks: [
      { block_type: 'header', content_pl: 'Zatwierdzenie', content_en: 'Approval', is_bold: true, font_size: 14 },
      { block_type: 'approval_signatures', content_pl: '', content_en: '', settings: { signatureSpacing: 30 } },
    ]
  },
};

const BLOCK_TYPES = [
  { value: 'logo', label: 'Header & Logo', icon: ImageIcon },
  { value: 'document_control', label: 'Document Control', icon: FileText },
  { value: 'header', label: 'Header/Title', icon: Type },
  { value: 'project_info', label: 'Project Information', icon: FileText },
  { value: 'assessment_info', label: 'Assessment Information', icon: FileText },
  { value: 'assessment_team', label: 'Assessment Team', icon: Users },
  { value: 'hazard', label: 'Hazard Identification', icon: AlertOctagon },
  { value: 'risk_matrix', label: 'Risk Matrix', icon: Shield },
  { value: 'control_measures', label: 'Control Measures', icon: CheckSquare },
  { value: 'ppe_requirements', label: 'PPE Requirements', icon: HardHat },
  { value: 'emergency_procedures', label: 'Emergency Procedures', icon: AlertCircle },
  { value: 'checklist', label: 'Checklist', icon: Clipboard },
  { value: 'rich_text', label: 'Rich Text', icon: FileText },
  { value: 'table', label: 'Custom Table', icon: FileText },
  { value: 'image', label: 'Image/Diagram', icon: ImageIcon },
  { value: 'approval_signatures', label: 'Approval Signatures', icon: FileText },
  { value: 'declaration', label: 'Declaration', icon: Clipboard },
  { value: 'divider', label: 'Divider Line', icon: Minus },
  { value: 'spacer', label: 'Spacer', icon: MoreVertical },
  { value: 'page_break', label: 'Page Break', icon: FileText },
];

const TEMPLATE_TYPES = [
  { value: 'wind_turbine', label: 'Wind Turbine' },
  { value: 'wind_general', label: 'Wind - General' },
  { value: 'solar', label: 'Solar' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'working_at_height', label: 'Working at Height' },
  { value: 'confined_space', label: 'Confined Space' },
  { value: 'general', label: 'General' },
];

const AVAILABLE_VARIABLES = [
  { name: 'company_name', label: 'Company Name' },
  { name: 'company_address', label: 'Company Address' },
  { name: 'assessment_number', label: 'Assessment Number' },
  { name: 'assessment_title', label: 'Assessment Title' },
  { name: 'location', label: 'Location' },
  { name: 'work_activity', label: 'Work Activity' },
  { name: 'assessment_date', label: 'Assessment Date' },
  { name: 'review_date', label: 'Review Date' },
  { name: 'expiry_date', label: 'Expiry Date' },
  { name: 'assessor_name', label: 'Assessor Name' },
  { name: 'reviewer_name', label: 'Reviewer Name' },
  { name: 'approver_name', label: 'Approver Name' },
  { name: 'version', label: 'Version' },
  { name: 'current_date', label: 'Current Date' },
];

const RiskAssessmentBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Template state
  const [template, setTemplate] = useState({
    name: '',
    template_type: 'general',
    description: '',
    is_active: true,
    page_margin_top: 20,
    page_margin_bottom: 20,
    page_margin_left: 20,
    page_margin_right: 20,
    include_polish: false,
    include_english: true,
  });

  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPresets, setShowPresets] = useState(false);

  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const historyDebounceRef = useRef(null);

  // Live preview
  const [showLivePreview, setShowLivePreview] = useState(true);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // Validation warnings
  const [warnings, setWarnings] = useState([]);

  // Logo upload
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // Load template if editing
  useEffect(() => {
    if (isEditing) {
      loadTemplate();
    }
  }, [id]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
      if (e.key === 'Delete' && selectedBlockId && !e.target.closest('input, textarea, [contenteditable]')) {
        e.preventDefault();
        handleDeleteBlock(selectedBlockId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBlockId, historyIndex, history]);

  // Auto-save with debounce
  useEffect(() => {
    if (hasUnsavedChanges && isEditing) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave(true);
      }, 30000); // Auto-save after 30 seconds
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, blocks, template]);

  // Validate template
  useEffect(() => {
    const newWarnings = [];

    if (!template.name) {
      newWarnings.push('Template name is required');
    }

    if (blocks.length === 0) {
      newWarnings.push('Template has no blocks');
    }

    const hasLogo = blocks.some(b => b.block_type === 'logo');
    if (!hasLogo) {
      newWarnings.push('No logo block - consider adding one');
    }

    const hasApproval = blocks.some(b => b.block_type === 'approval_signatures');
    if (!hasApproval) {
      newWarnings.push('No approval signatures block');
    }

    setWarnings(newWarnings);
  }, [template, blocks]);

  // Resize panel handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const containerRect = resizeRef.current?.parentElement?.getBoundingClientRect();
      if (containerRect) {
        const newWidth = containerRect.right - e.clientX;
        setPreviewWidth(Math.max(300, Math.min(800, newWidth)));
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Load preview when toggled or blocks change
  useEffect(() => {
    if (showLivePreview && isEditing) {
      loadPreview();
    }
  }, [showLivePreview, blocks, template, isEditing]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/risk-assessment-templates/${id}/`);
      const data = response.data;

      setTemplate({
        name: data.name,
        template_type: data.template_type,
        description: data.description || '',
        is_active: data.is_active,
        page_margin_top: data.page_margin_top,
        page_margin_bottom: data.page_margin_bottom,
        page_margin_left: data.page_margin_left,
        page_margin_right: data.page_margin_right,
        include_polish: data.include_polish,
        include_english: data.include_english,
      });

      const loadedBlocks = (data.blocks || []).map((block, index) => ({
        ...block,
        id: block.id || `block-${Date.now()}-${index}`,
        settings: block.settings || {},
      }));

      setBlocks(loadedBlocks);
      pushHistory(loadedBlocks);

      // Load logo preview if exists
      if (data.logo) {
        // Handle both relative and absolute URLs
        const logoUrl = data.logo.startsWith('http')
          ? data.logo
          : `${process.env.REACT_APP_API_URL}${data.logo}`;
        setLogoPreview(logoUrl);
      }

    } catch (err) {
      setError('Failed to load template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    if (!isEditing) return;

    setPreviewLoading(true);
    try {
      // First save template settings (without logo - that requires separate save)
      await api.patch(`/risk-assessment-templates/${id}/`, {
        name: template.name,
        template_type: template.template_type,
        description: template.description || '',
        is_active: template.is_active,
        page_margin_top: template.page_margin_top,
        page_margin_bottom: template.page_margin_bottom,
        page_margin_left: template.page_margin_left,
        page_margin_right: template.page_margin_right,
        include_polish: template.include_polish,
        include_english: template.include_english,
      });

      // Save blocks
      await api.post(`/risk-assessment-templates/${id}/update_blocks/`, {
        blocks: blocks.map((block, index) => ({
          block_type: block.block_type,
          order: index,
          content_pl: block.content_pl || '',
          content_en: block.content_en || '',
          alignment: block.alignment || 'left',
          font_size: block.font_size || 10,
          is_bold: block.is_bold || false,
          margin_top: block.margin_top || 0,
          margin_bottom: block.margin_bottom || 5,
          settings: block.settings || {},
          is_visible: block.is_visible !== false,
        }))
      });

      // Then get the preview
      const response = await api.get(`/risk-assessment-templates/${id}/preview/`, {
        responseType: 'blob'
      });

      const url = URL.createObjectURL(response.data);
      setPreviewPdf(url);
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const pushHistory = (newBlocks) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newBlocks));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setBlocks(JSON.parse(history[newIndex]));
      setHasUnsavedChanges(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setBlocks(JSON.parse(history[newIndex]));
      setHasUnsavedChanges(true);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setHasUnsavedChanges(true);
    }
  };

  const handleSave = async (isAutoSave = false) => {
    setSaving(true);
    setError(null);

    try {
      let templateId = id;

      // Use FormData if there's a logo file
      if (logoFile) {
        const formData = new FormData();
        formData.append('name', template.name);
        formData.append('template_type', template.template_type);
        formData.append('description', template.description || '');
        formData.append('is_active', template.is_active);
        formData.append('page_margin_top', template.page_margin_top);
        formData.append('page_margin_bottom', template.page_margin_bottom);
        formData.append('page_margin_left', template.page_margin_left);
        formData.append('page_margin_right', template.page_margin_right);
        formData.append('include_polish', template.include_polish);
        formData.append('include_english', template.include_english);
        formData.append('logo', logoFile);

        if (isEditing) {
          await api.patch(`/risk-assessment-templates/${id}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          const response = await api.post('/risk-assessment-templates/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          templateId = response.data.id;
        }
        setLogoFile(null); // Clear after upload
      } else {
        const templateData = { ...template };

        if (isEditing) {
          await api.patch(`/risk-assessment-templates/${id}/`, templateData);
        } else {
          const response = await api.post('/risk-assessment-templates/', templateData);
          templateId = response.data.id;
        }
      }

      // Save blocks
      await api.post(`/risk-assessment-templates/${templateId}/update_blocks/`, {
        blocks: blocks.map((block, index) => ({
          block_type: block.block_type,
          order: index,
          content_pl: block.content_pl || '',
          content_en: block.content_en || '',
          alignment: block.alignment || 'left',
          font_size: block.font_size || 10,
          is_bold: block.is_bold || false,
          margin_top: block.margin_top || 0,
          margin_bottom: block.margin_bottom || 5,
          settings: block.settings || {},
          is_visible: block.is_visible !== false,
        }))
      });

      setHasUnsavedChanges(false);
      setLastSaved(new Date());

      // Reset history after save - undo will only go back to this saved state
      setHistory([JSON.stringify(blocks)]);
      setHistoryIndex(0);

      if (!isEditing && !isAutoSave) {
        navigate(`/risk-assessment-templates/${templateId}/edit`);
      }

    } catch (err) {
      setError('Failed to save template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = (blockType) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      block_type: blockType,
      content_pl: '',
      content_en: '',
      alignment: 'left',
      font_size: blockType === 'header' ? 14 : 10,
      is_bold: blockType === 'header',
      margin_top: 0,
      margin_bottom: 5,
      settings: {},
      is_visible: true,
    };

    const newBlocks = [...blocks, newBlock];
    setBlocks(newBlocks);
    setSelectedBlockId(newBlock.id);
    setHasUnsavedChanges(true);
    pushHistory(newBlocks);
  };

  const handleAddPreset = (presetKey) => {
    const preset = BLOCK_PRESETS[presetKey];
    if (!preset) return;

    const newBlocks = preset.blocks.map((block, index) => ({
      ...block,
      id: `block-${Date.now()}-${index}`,
      alignment: block.alignment || 'left',
      margin_top: block.margin_top || 0,
      margin_bottom: block.margin_bottom || 5,
      settings: block.settings || {},
      is_visible: true,
    }));

    const updatedBlocks = [...blocks, ...newBlocks];
    setBlocks(updatedBlocks);
    setHasUnsavedChanges(true);
    pushHistory(updatedBlocks);
    setShowPresets(false);
  };

  const handleDeleteBlock = (blockId) => {
    const newBlocks = blocks.filter(b => b.id !== blockId);
    setBlocks(newBlocks);
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
    setHasUnsavedChanges(true);
    pushHistory(newBlocks);
  };

  const handleDuplicateBlock = (blockId) => {
    const blockIndex = blocks.findIndex(b => b.id === blockId);
    if (blockIndex === -1) return;

    const block = blocks[blockIndex];
    const newBlock = {
      ...block,
      id: `block-${Date.now()}`,
    };

    const newBlocks = [...blocks];
    newBlocks.splice(blockIndex + 1, 0, newBlock);
    setBlocks(newBlocks);
    setSelectedBlockId(newBlock.id);
    setHasUnsavedChanges(true);
    pushHistory(newBlocks);
  };

  const handleUpdateBlock = (blockId, updates) => {
    const newBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, ...updates } : block
    );
    setBlocks(newBlocks);
    setHasUnsavedChanges(true);

    // Debounced history push for undo/redo
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
    }
    historyDebounceRef.current = setTimeout(() => {
      pushHistory(newBlocks);
    }, 1000); // Push to history after 1 second of no changes
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(blocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBlocks(items);
    setHasUnsavedChanges(true);
    pushHistory(items);
  };

  const insertVariable = (variable) => {
    if (!selectedBlock) return;

    const content = selectedBlock.content_pl || '';
    const newContent = content + `{{${variable}}}`;
    handleUpdateBlock(selectedBlock.id, { content_pl: newContent });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div
        className="bg-white border-b px-6 py-4 flex items-center justify-between"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setSelectedBlockId(null);
          }
        }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/risk-assessment-templates')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold">
              {isEditing ? 'Edit Risk Assessment Template' : 'Create Risk Assessment Template'}
            </h1>
            {hasUnsavedChanges && (
              <span className="text-xs text-amber-600">Unsaved changes</span>
            )}
            {lastSaved && !hasUnsavedChanges && (
              <span className="text-xs text-green-600">
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title="Undo (Ctrl+Z)"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title="Redo (Ctrl+Y)"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="relative group">
              <button className="p-2 hover:bg-amber-50 rounded-lg text-amber-600">
                <AlertTriangle className="w-4 h-4" />
              </button>
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border p-3 hidden group-hover:block z-50">
                <div className="text-xs font-medium mb-2">Warnings</div>
                {warnings.map((warning, i) => (
                  <div key={i} className="text-xs text-amber-600 mb-1">
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview toggle */}
          {isEditing && (
            <button
              onClick={() => setShowLivePreview(!showLivePreview)}
              className={`p-2 rounded-lg flex items-center gap-2 ${
                showLivePreview ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'
              }`}
              title="Toggle Preview"
            >
              {showLivePreview ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
              <span className="text-sm">Preview</span>
            </button>
          )}

          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Block types */}
        <div className="w-64 bg-white border-r overflow-y-auto p-4">
          <h3 className="font-medium mb-3">Add Block</h3>
          <div className="space-y-1">
            {BLOCK_TYPES.map(type => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => handleAddBlock(type.value)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg"
                >
                  <IconComponent className="w-4 h-4 text-gray-500" />
                  {type.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-gray-100 rounded-lg"
            >
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Block Presets
              </span>
              <Plus className={`w-4 h-4 transition-transform ${showPresets ? 'rotate-45' : ''}`} />
            </button>

            {showPresets && (
              <div className="mt-2 space-y-1">
                {Object.entries(BLOCK_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => handleAddPreset(key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-blue-50 rounded-lg text-blue-600"
                  >
                    <Plus className="w-3 h-3" />
                    {preset.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Variables */}
          <div className="mt-6">
            <h3 className="font-medium mb-3">Variables</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {AVAILABLE_VARIABLES.map(variable => (
                <button
                  key={variable.name}
                  onClick={() => insertVariable(variable.name)}
                  disabled={!selectedBlock}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <code className="text-blue-600">{`{{${variable.name}}}`}</code>
                  <div className="text-gray-500">{variable.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Center - Block list and editor */}
        <div className={`flex-1 flex overflow-hidden ${showLivePreview ? '' : ''}`}>
          <div
            className="flex-1 overflow-y-auto p-6"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedBlockId(null);
              }
            }}
          >
            {/* Template settings */}
            <div className="bg-white rounded-lg border mb-6">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setShowTemplateSettings(!showTemplateSettings)}
              >
                <h3 className="font-medium">Template Settings</h3>
                {showTemplateSettings ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
              {showTemplateSettings && (
              <div className="grid grid-cols-2 gap-4 p-4 pt-0 border-t">
                <div>
                  <label className="block text-sm font-medium mb-1">Template Name</label>
                  <input
                    type="text"
                    value={template.name}
                    onChange={(e) => {
                      setTemplate({ ...template, name: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Template Type</label>
                  <select
                    value={template.template_type}
                    onChange={(e) => {
                      setTemplate({ ...template, template_type: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {TEMPLATE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={template.description}
                    onChange={(e) => {
                      setTemplate({ ...template, description: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                  />
                </div>
                <div className="col-span-2 grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Top Margin (mm)</label>
                    <input
                      type="number"
                      value={template.page_margin_top}
                      onChange={(e) => {
                        setTemplate({ ...template, page_margin_top: parseInt(e.target.value) || 0 });
                        setHasUnsavedChanges(true);
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bottom Margin (mm)</label>
                    <input
                      type="number"
                      value={template.page_margin_bottom}
                      onChange={(e) => {
                        setTemplate({ ...template, page_margin_bottom: parseInt(e.target.value) || 0 });
                        setHasUnsavedChanges(true);
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Left Margin (mm)</label>
                    <input
                      type="number"
                      value={template.page_margin_left}
                      onChange={(e) => {
                        setTemplate({ ...template, page_margin_left: parseInt(e.target.value) || 0 });
                        setHasUnsavedChanges(true);
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Right Margin (mm)</label>
                    <input
                      type="number"
                      value={template.page_margin_right}
                      onChange={(e) => {
                        setTemplate({ ...template, page_margin_right: parseInt(e.target.value) || 0 });
                        setHasUnsavedChanges(true);
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div className="col-span-2 flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={template.is_active}
                      onChange={(e) => {
                        setTemplate({ ...template, is_active: e.target.checked });
                        setHasUnsavedChanges(true);
                      }}
                      className="rounded"
                    />
                    Active
                  </label>
                </div>
              </div>
              )}
            </div>

            {/* Blocks */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="blocks">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                    {blocks.map((block, index) => {
                      // Calculate page number for this block
                      let pageNumber = 1;
                      for (let i = 0; i < index; i++) {
                        if (blocks[i].block_type === 'page_break') {
                          pageNumber++;
                        }
                      }

                      // Check if previous block was a page_break
                      const showPageIndicator = index > 0 && blocks[index - 1].block_type === 'page_break';

                      return (
                        <React.Fragment key={block.id}>
                          {showPageIndicator && (
                            <div className="flex items-center gap-2 py-2">
                              <div className="flex-1 border-t-2 border-dashed border-blue-300"></div>
                              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                Page {pageNumber}
                              </span>
                              <div className="flex-1 border-t-2 border-dashed border-blue-300"></div>
                            </div>
                          )}
                          <Draggable draggableId={String(block.id)} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-white rounded-lg border ${
                                  selectedBlockId === block.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                                } ${snapshot.isDragging ? 'shadow-lg' : 'shadow-sm'}`}
                              >
                            <div
                              className="flex items-center gap-2 px-3 py-2 border-b bg-white cursor-pointer"
                              onClick={() => setSelectedBlockId(selectedBlockId === block.id ? null : block.id)}
                            >
                              <div {...provided.dragHandleProps} className="cursor-grab">
                                <GripVertical className="w-4 h-4 text-gray-400" />
                              </div>
                              <span className="text-sm font-medium flex-1">
                                {BLOCK_TYPES.find(t => t.value === block.block_type)?.label || block.block_type}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateBlock(block.id, { is_visible: !block.is_visible });
                                }}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {block.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateBlock(block.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded"
                                title="Duplicate"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBlock(block.id);
                                }}
                                className="p-1 hover:bg-red-100 rounded text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {selectedBlockId === block.id && (
                              <div className="p-4">
                                <BlockEditor
                                  block={block}
                                  onUpdate={(updates) => handleUpdateBlock(block.id, updates)}
                                  onLogoChange={handleLogoChange}
                                  logoPreview={logoPreview}
                                  onLogoRemove={() => {
                                    setLogoFile(null);
                                    setLogoPreview(null);
                                    setHasUnsavedChanges(true);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                        </React.Fragment>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {blocks.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No blocks yet. Add blocks from the sidebar to build your template.</p>
              </div>
            )}
          </div>

          {/* Live Preview Panel */}
          {showLivePreview && (
            <>
              {/* Resize handle */}
              <div
                ref={resizeRef}
                className="w-2 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex items-center justify-center group"
                onMouseDown={() => setIsResizing(true)}
              >
                <div className="w-1 h-8 bg-gray-400 group-hover:bg-blue-600 rounded" />
              </div>

              <div
                className="bg-gray-100 border-l overflow-hidden flex flex-col"
                style={{ width: previewWidth }}
              >
                <div className="p-3 bg-white border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Live Preview</span>
                  <button
                    onClick={loadPreview}
                    disabled={previewLoading}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {previewLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-2">
                  {previewPdf ? (
                    <iframe
                      src={previewPdf}
                      className="w-full h-full border rounded bg-white"
                      title="Preview"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                      {previewLoading ? 'Loading preview...' : 'Save to see preview'}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Block Editor Component
const BlockEditor = ({ block, onUpdate, onLogoChange, logoPreview, onLogoRemove }) => {
  const blockType = block.block_type;

  // Rich text editor for content blocks
  const renderContentEditor = () => {
    if (['divider', 'spacer', 'page_break', 'logo', 'risk_matrix', 'hazard', 'approval_signatures', 'declaration'].includes(blockType)) {
      return null;
    }

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Content</label>
          {['rich_text', 'project_info', 'assessment_info', 'emergency_procedures'].includes(blockType) ? (
            <>
              <ReactQuill
                value={block.content_en || ''}
                onChange={(value, delta, source, editor) => {
                  // Preserve scroll positions
                  const editorEl = document.querySelector('.ql-editor');
                  const editorScroll = editorEl?.scrollTop;
                  const pageScroll = window.scrollY;

                  onUpdate({ content_en: value });

                  // Restore scroll positions after React updates
                  requestAnimationFrame(() => {
                    if (editorEl && editorScroll) {
                      editorEl.scrollTop = editorScroll;
                    }
                    window.scrollTo(0, pageScroll);
                  });
                }}
                className="bg-white"
                style={{ minHeight: '200px' }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic', 'underline'],
                    [{ 'align': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['clean']
                  ],
                  clipboard: {
                    matchVisual: false
                  }
                }}
                preserveWhitespace={true}
              />
              <style>{`
                .ql-container {
                  min-height: 200px;
                  overflow-y: auto;
                  resize: vertical;
                }
                .ql-editor {
                  min-height: 200px;
                  overflow-y: auto;
                }
                .ql-clipboard {
                  position: fixed !important;
                  left: -100000px !important;
                }
              `}</style>
            </>
          ) : (
            <input
              type="text"
              value={block.content_en || ''}
              onChange={(e) => onUpdate({ content_en: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          )}
        </div>
      </div>
    );
  };

  // Settings based on block type
  const renderSettings = () => {
    const settings = block.settings || {};

    return (
      <div className="mt-4 pt-4 border-t">
        {blockType === 'logo' && (
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={settings.repeatOnEveryPage || false}
                onChange={(e) => onUpdate({
                  settings: { ...settings, repeatOnEveryPage: e.target.checked }
                })}
              />
              Repeat on every page
            </label>
          </div>
        )}
        <h4 className="text-sm font-medium mb-3">Block Settings</h4>

        <div className="grid grid-cols-2 gap-4">
          {!['divider', 'spacer', 'page_break'].includes(blockType) && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1">Font Size</label>
                <input
                  type="number"
                  value={block.font_size || 10}
                  onChange={(e) => onUpdate({ font_size: parseInt(e.target.value) || 10 })}
                  className="w-full px-2 py-1 text-sm border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Alignment</label>
                <select
                  value={block.alignment || 'left'}
                  onChange={(e) => onUpdate({ alignment: e.target.value })}
                  className="w-full px-2 py-1 text-sm border rounded"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={block.is_bold || false}
                    onChange={(e) => onUpdate({ is_bold: e.target.checked })}
                  />
                  Bold
                </label>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium mb-1">Margin Top (mm)</label>
            <input
              type="number"
              value={block.margin_top || 0}
              onChange={(e) => onUpdate({ margin_top: parseInt(e.target.value) || 0 })}
              className="w-full px-2 py-1 text-sm border rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Margin Bottom (mm)</label>
            <input
              type="number"
              value={block.margin_bottom || 0}
              onChange={(e) => onUpdate({ margin_bottom: parseInt(e.target.value) || 0 })}
              className="w-full px-2 py-1 text-sm border rounded"
            />
          </div>
        </div>

        {/* Block-specific settings */}
        {blockType === 'spacer' && (
          <div className="mt-3">
            <label className="block text-xs font-medium mb-1">Height (mm)</label>
            <input
              type="number"
              value={settings.height || 20}
              onChange={(e) => onUpdate({
                settings: { ...settings, height: parseInt(e.target.value) || 20 }
              })}
              className="w-full px-2 py-1 text-sm border rounded"
            />
          </div>
        )}

        {blockType === 'divider' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Thickness</label>
              <input
                type="number"
                value={settings.thickness || 1}
                onChange={(e) => onUpdate({
                  settings: { ...settings, thickness: parseInt(e.target.value) || 1 }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Color</label>
              <input
                type="color"
                value={settings.color || '#000000'}
                onChange={(e) => onUpdate({
                  settings: { ...settings, color: e.target.value }
                })}
                className="w-full h-8 border rounded"
              />
            </div>
          </div>
        )}

        {blockType === 'logo' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Logo Image</label>
              <div className="flex items-center gap-3">
                {logoPreview && (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-12 w-auto object-contain border rounded"
                    />
                    <button
                      onClick={onLogoRemove}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onLogoChange}
                  className="text-xs"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Document Title</label>
              <input
                type="text"
                value={settings.documentTitle || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, documentTitle: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="RISK ASSESSMENT METHOD OF STATEMENT"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Project</label>
              <input
                type="text"
                value={settings.project || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, project: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Project name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Issued by</label>
              <input
                type="text"
                value={settings.issuedBy || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, issuedBy: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Client</label>
              <input
                type="text"
                value={settings.client || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, client: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Client name"
              />
            </div>

            {/* Logo Settings */}
            <div className="pt-3 border-t">
              <label className="block text-xs font-semibold mb-2 text-gray-700">Logo Settings</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Logo Max Width (px)</label>
                <input
                  type="number"
                  value={settings.maxWidth || 150}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, maxWidth: parseInt(e.target.value) || 150 }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Logo Max Height (px)</label>
                <input
                  type="number"
                  value={settings.maxHeight || 80}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, maxHeight: parseInt(e.target.value) || 80 }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                />
              </div>
            </div>
          </div>
        )}

        {blockType === 'document_control' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Title (optional)</label>
              <input
                type="text"
                value={settings.title || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, title: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="e.g. Document Control"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Project Name</label>
              <input
                type="text"
                value={settings.projectName || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, projectName: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Project name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Prepared by</label>
                <input
                  type="text"
                  value={settings.preparedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, preparedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Authorised for issue by</label>
                <input
                  type="text"
                  value={settings.authorisedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, authorisedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Approved by</label>
                <input
                  type="text"
                  value={settings.approvedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, approvedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Issue Date</label>
                <input
                  type="text"
                  value={settings.issueDate || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, issueDate: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="DD.MM.YYYY"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Issue No.</label>
                <input
                  type="text"
                  value={settings.issueNo || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, issueNo: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Rev.1.0"
                />
              </div>
            </div>
          </div>
        )}

        {blockType === 'project_info' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Document Title</label>
              <input
                type="text"
                value={settings.documentTitle || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, documentTitle: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="e.g. OFFLOADING COMPONENTS"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Project Reference</label>
              <input
                type="text"
                value={settings.projectRef || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, projectRef: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="e.g. KLEUTERBACH SP-65123"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Project Name</label>
              <input
                type="text"
                value={settings.project || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, project: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Project name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Issued by</label>
                <input
                  type="text"
                  value={settings.issuedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, issuedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Client</label>
                <input
                  type="text"
                  value={settings.client || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, client: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Client name"
                />
              </div>
            </div>

            {/* Document Control Section */}
            <div className="pt-3 border-t">
              <label className="block text-xs font-semibold mb-2 text-gray-700">Document Control</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Prepared by</label>
                <input
                  type="text"
                  value={settings.preparedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, preparedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Authorised for issue by</label>
                <input
                  type="text"
                  value={settings.authorisedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, authorisedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Approved by</label>
                <input
                  type="text"
                  value={settings.approvedBy || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, approvedBy: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Issue Date</label>
                <input
                  type="text"
                  value={settings.issueDate || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, issueDate: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="DD.MM.YYYY"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Issue No.</label>
                <input
                  type="text"
                  value={settings.issueNo || ''}
                  onChange={(e) => onUpdate({
                    settings: { ...settings, issueNo: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="Rev.1.0"
                />
              </div>
            </div>
          </div>
        )}

        {blockType === 'approval_signatures' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Approved by (Name)</label>
              <input
                type="text"
                value={settings.approverName || ''}
                onChange={(e) => onUpdate({
                  settings: { ...settings, approverName: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Enter name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Statement Text</label>
              <textarea
                value={settings.statement || 'All control measures are available and have been implemented. I confirm that I have read and understand the above risk assessment:'}
                onChange={(e) => onUpdate({
                  settings: { ...settings, statement: e.target.value }
                })}
                className="w-full px-2 py-1 text-xs border rounded"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Signature Spacing (px)</label>
              <input
                type="number"
                value={settings.signatureSpacing || 25}
                onChange={(e) => onUpdate({
                  settings: { ...settings, signatureSpacing: parseInt(e.target.value) || 25 }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-2">Positions</label>
              <div className="space-y-2">
                {(settings.positions || [
                  { position: 'Installation Leader:', name: '' },
                  { position: 'Installation Leader:', name: '' }
                ]).map((pos, index) => (
                  <div key={index} className="flex gap-1 items-center">
                    <input
                      type="text"
                      value={pos.position || ''}
                      onChange={(e) => {
                        const newPositions = [...(settings.positions || [
                          { position: 'Installation Leader:', name: '' },
                          { position: 'Installation Leader:', name: '' }
                        ])];
                        newPositions[index] = { ...pos, position: e.target.value };
                        onUpdate({ settings: { ...settings, positions: newPositions } });
                      }}
                      className="flex-1 px-2 py-1 text-xs border rounded"
                      placeholder="Position"
                    />
                    <input
                      type="text"
                      value={pos.name || ''}
                      onChange={(e) => {
                        const newPositions = [...(settings.positions || [
                          { position: 'Installation Leader:', name: '' },
                          { position: 'Installation Leader:', name: '' }
                        ])];
                        newPositions[index] = { ...pos, name: e.target.value };
                        onUpdate({ settings: { ...settings, positions: newPositions } });
                      }}
                      className="flex-1 px-2 py-1 text-xs border rounded"
                      placeholder="Name"
                    />
                    <button
                      onClick={() => {
                        const newPositions = (settings.positions || []).filter((_, i) => i !== index);
                        onUpdate({ settings: { ...settings, positions: newPositions } });
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newPositions = [...(settings.positions || []), { position: '', name: '' }];
                    onUpdate({ settings: { ...settings, positions: newPositions } });
                  }}
                  className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                >
                  + Add Position
                </button>
              </div>
            </div>
          </div>
        )}

        {blockType === 'table' && (
          <div className="mt-3">
            <label className="block text-xs font-medium mb-2">Table Data</label>
            <div className="space-y-2">
              {(settings.data || [['', ''], ['', '']]).map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-1 items-center">
                  {row.map((cell, colIndex) => (
                    <input
                      key={colIndex}
                      type="text"
                      value={cell}
                      onChange={(e) => {
                        const newData = [...(settings.data || [['', ''], ['', '']])];
                        newData[rowIndex][colIndex] = e.target.value;
                        onUpdate({ settings: { ...settings, data: newData } });
                      }}
                      className="flex-1 px-2 py-1 text-xs border rounded"
                      placeholder={rowIndex === 0 ? 'Header' : 'Cell'}
                    />
                  ))}
                  <button
                    onClick={() => {
                      const newData = (settings.data || [['', ''], ['', '']]).filter((_, i) => i !== rowIndex);
                      onUpdate({ settings: { ...settings, data: newData } });
                    }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Remove row"
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const currentData = settings.data || [['', ''], ['', '']];
                    const cols = currentData[0]?.length || 2;
                    const newRow = Array(cols).fill('');
                    onUpdate({ settings: { ...settings, data: [...currentData, newRow] } });
                  }}
                  className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                >
                  + Add Row
                </button>
                <button
                  onClick={() => {
                    const currentData = settings.data || [['', ''], ['', '']];
                    const newData = currentData.map(row => [...row, '']);
                    onUpdate({ settings: { ...settings, data: newData } });
                  }}
                  className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100"
                >
                  + Add Column
                </button>
                <button
                  onClick={() => {
                    const currentData = settings.data || [['', ''], ['', '']];
                    if (currentData[0]?.length > 1) {
                      const newData = currentData.map(row => row.slice(0, -1));
                      onUpdate({ settings: { ...settings, data: newData } });
                    }
                  }}
                  className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100"
                >
                  - Remove Column
                </button>
              </div>
            </div>
          </div>
        )}

        {blockType === 'hazard' && (
          <div className="mt-3">
            <label className="block text-xs font-medium mb-2">Hazards</label>
            <div className="space-y-3">
              {(settings.hazards || []).map((hazard, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded border space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium">Hazard {index + 1}</span>
                    <button
                      onClick={() => {
                        const newHazards = settings.hazards.filter((_, i) => i !== index);
                        onUpdate({ settings: { ...settings, hazards: newHazards } });
                      }}
                      className="text-red-500 text-xs hover:bg-red-50 px-2 py-1 rounded"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    type="text"
                    value={hazard.name || ''}
                    onChange={(e) => {
                      const newHazards = [...settings.hazards];
                      newHazards[index] = { ...hazard, name: e.target.value };
                      onUpdate({ settings: { ...settings, hazards: newHazards } });
                    }}
                    className="w-full px-2 py-1 text-xs border rounded"
                    placeholder="Hazard name"
                  />
                  <textarea
                    value={hazard.effect || ''}
                    onChange={(e) => {
                      const newHazards = [...settings.hazards];
                      newHazards[index] = { ...hazard, effect: e.target.value };
                      onUpdate({ settings: { ...settings, hazards: newHazards } });
                    }}
                    className="w-full px-2 py-1 text-xs border rounded"
                    rows={2}
                    placeholder="Hazard effect"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs mb-1">Severity (1-5)</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={hazard.severity || ''}
                        onChange={(e) => {
                          const newHazards = [...settings.hazards];
                          newHazards[index] = { ...hazard, severity: e.target.value };
                          onUpdate({ settings: { ...settings, hazards: newHazards } });
                        }}
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Likelihood (1-5)</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={hazard.likelihood || ''}
                        onChange={(e) => {
                          const newHazards = [...settings.hazards];
                          newHazards[index] = { ...hazard, likelihood: e.target.value };
                          onUpdate({ settings: { ...settings, hazards: newHazards } });
                        }}
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Risk (auto)</label>
                      <div className="px-2 py-1 text-xs bg-gray-100 border rounded text-center">
                        {hazard.severity && hazard.likelihood
                          ? parseInt(hazard.severity) * parseInt(hazard.likelihood)
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <textarea
                    value={hazard.control_measures || ''}
                    onChange={(e) => {
                      const newHazards = [...settings.hazards];
                      newHazards[index] = { ...hazard, control_measures: e.target.value };
                      onUpdate({ settings: { ...settings, hazards: newHazards } });
                    }}
                    className="w-full px-2 py-1 text-xs border rounded"
                    rows={3}
                    placeholder="Control measures"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs mb-1">Residual S</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={hazard.residual_severity || ''}
                        onChange={(e) => {
                          const newHazards = [...settings.hazards];
                          newHazards[index] = { ...hazard, residual_severity: e.target.value };
                          onUpdate({ settings: { ...settings, hazards: newHazards } });
                        }}
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Residual L</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={hazard.residual_likelihood || ''}
                        onChange={(e) => {
                          const newHazards = [...settings.hazards];
                          newHazards[index] = { ...hazard, residual_likelihood: e.target.value };
                          onUpdate({ settings: { ...settings, hazards: newHazards } });
                        }}
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Residual Risk</label>
                      <div className="px-2 py-1 text-xs bg-gray-100 border rounded text-center">
                        {hazard.residual_severity && hazard.residual_likelihood
                          ? parseInt(hazard.residual_severity) * parseInt(hazard.residual_likelihood)
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  const newHazards = [...(settings.hazards || []), {
                    name: '',
                    effect: '',
                    severity: '',
                    likelihood: '',
                    control_measures: '',
                    residual_severity: '',
                    residual_likelihood: ''
                  }];
                  onUpdate({ settings: { ...settings, hazards: newHazards } });
                }}
                className="w-full text-xs px-2 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                + Add Hazard
              </button>
            </div>
          </div>
        )}

        {blockType === 'declaration' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Title</label>
              <input
                type="text"
                value={settings.title || 'Declaration of reading and understanding OmegaWind occupational risk assessment:'}
                onChange={(e) => onUpdate({
                  settings: { ...settings, title: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Subtitle</label>
              <input
                type="text"
                value={settings.subtitle || '(will be signed during site mobilization)'}
                onChange={(e) => onUpdate({
                  settings: { ...settings, subtitle: e.target.value }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Number of Rows</label>
              <input
                type="number"
                min="1"
                max="50"
                value={settings.rowCount || 15}
                onChange={(e) => onUpdate({
                  settings: { ...settings, rowCount: parseInt(e.target.value) || 15 }
                })}
                className="w-full px-2 py-1 text-sm border rounded"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {renderContentEditor()}
      {renderSettings()}
    </div>
  );
};

export default RiskAssessmentBuilder;
